import {
  sessionlessEvidenceAwarenessDevForceShowAtom,
  sessionlessEvidenceAwarenessDevVariantOverrideAtom,
} from '@/atoms/sessionlessEvidenceAwarenessDevAtoms';
import type { EvidenceAwarenessPopupVariant } from '@/features/flags/legacy-flags';
import { GET_EVIDENCE_AWARENESS_POPUP_VARIANT } from '@/features/flags/legacy-flags';
import { ENV_NAME, getEnvironment } from '@heidi/api/config';
import { Button } from '@heidi/design-system/button';
import {
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from '@heidi/design-system/segmented-control';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useAtom } from 'jotai';
import { useCallback } from 'react';

type VariantSegmentValue = 'default' | EvidenceAwarenessPopupVariant;

const VARIANT_SEGMENTS: { value: VariantSegmentValue; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'control', label: 'control' },
  { value: 'floating', label: 'floating' },
  { value: 'sidebar', label: 'sidebar' },
];

interface SessionlessEvidenceAwarenessDevSectionProps {
  onRequestClose?: () => void;
}

/**
 * Dev-tools controls for sessionless Evidence awareness (multivariate variant override
 * + force-show preview). Cadence is enforced via `modalClosedRules` cooldown.
 */
export function SessionlessEvidenceAwarenessDevSection({
  onRequestClose,
}: SessionlessEvidenceAwarenessDevSectionProps) {
  const [variantOverride, setVariantOverride] = useAtom(
    sessionlessEvidenceAwarenessDevVariantOverrideAtom
  );
  const [forceShow, setForceShow] = useAtom(sessionlessEvidenceAwarenessDevForceShowAtom);

  const isNonProd = getEnvironment() !== ENV_NAME.PROD;

  const resolvedVariant = GET_EVIDENCE_AWARENESS_POPUP_VARIANT();

  const segmentValue: VariantSegmentValue =
    variantOverride == null ? 'default' : variantOverride;

  const handleVariantChange = useCallback(
    (next: string) => {
      const v = next as VariantSegmentValue;
      if (v === 'default') {
        setVariantOverride(null);
      } else {
        setVariantOverride(v);
      }
    },
    [setVariantOverride]
  );

  const handleForceShow = useCallback(() => {
    setForceShow(true);
    if (variantOverride === null || variantOverride === 'control') {
      setVariantOverride('floating');
    }
    onRequestClose?.();
  }, [onRequestClose, setForceShow, setVariantOverride, variantOverride]);

  const handleClearForceShow = useCallback(() => {
    setForceShow(false);
  }, [setForceShow]);

  if (!isNonProd) {
    return null;
  }

  return (
    <section
      aria-labelledby="dev-tools-sessionless-evidence-awareness-heading"
      className="mt-4 border-t border-border pt-4"
    >
      <TypographyP2SemiBold
        id="dev-tools-sessionless-evidence-awareness-heading"
        className="mb-2"
      >
        Sessionless Evidence awareness
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <div className="min-w-0 space-y-1">
          <TypographyCaption className="text-text-secondary">
            Override <span className="font-mono">exp-evidence-awareness-popup</span>{' '}
            (multivariate)
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Resolved for UI:{' '}
            <span className="font-mono tabular-nums">{resolvedVariant}</span>
            {variantOverride != null ? ' (dev override)' : ''}
          </TypographyCaption>
        </div>
        <SegmentedControl value={segmentValue} onValueChange={handleVariantChange}>
          <SegmentedControlList className="grid w-full grid-cols-2 gap-1 sm:grid-cols-4">
            {VARIANT_SEGMENTS.map((opt) => (
              <SegmentedControlTrigger key={opt.value} value={opt.value}>
                {opt.label}
              </SegmentedControlTrigger>
            ))}
          </SegmentedControlList>
        </SegmentedControl>

        <div className="min-w-0 space-y-1">
          <TypographyCaption className="text-text-secondary">
            Fetch suggestions + card without normal eligibility
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Dev force-show:{' '}
            <span className="font-mono">{forceShow ? 'true' : 'false'}</span>
            {' · '}
            If variant is Default or control, Force show switches override to floating so
            the popover mounts.
          </TypographyCaption>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="pro"
            size="sm"
            className="flex-1"
            onClick={handleForceShow}
          >
            Force show
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleClearForceShow}
            disabled={!forceShow}
          >
            Clear force-show
          </Button>
        </div>

        <TypographyCaption className="text-text-tertiary">
          Cadence is enforced via the modal cooldown (every 4 days). To re-test the first
          impression, clear the user&rsquo;s{' '}
          <span className="font-mono">SESSIONLESS_EVIDENCE_AWARENESS</span> entry in{' '}
          <span className="font-mono">seen_modals</span>.
        </TypographyCaption>
      </div>
    </section>
  );
}
